import { PublicClientApplication } from "@azure/msal-browser";
import { setItem, removeItem } from "@maersk/shared-js";
import { getCurrentEnvironmentVariables } from "@/lib/CONSTANTS";
import store from "@/store/index.js";

/**
 * MSAL v3 Authentication Module
 * Provides Azure AD authentication using the authorization code flow with PKCE
 */

// Get redirect URI based on environment
function getRedirectUri() {
  // Explicit override from env
  if (process.env.VUE_APP_REDIRECT_URI) {
    return process.env.VUE_APP_REDIRECT_URI;
  }

  // Check if window is available (browser environment)
  if (typeof window !== "undefined" && window.location) {
    const currentHost = window.location.hostname;

    // Check if it's an Azure deployment (azurewebsites.net or its subdomains)
    if (currentHost === "azurewebsites.net" || currentHost.endsWith(".azurewebsites.net")) {
      return getCurrentEnvironmentVariables().azureRedirectUri;
    }
  }

  // Use the current environment configuration for other deployments
  return getCurrentEnvironmentVariables().redirectUri;
}

// MSAL Configuration
export const msalConfig = {
  auth: {
    clientId: process.env.VUE_APP_CLIENT_ID,
    authority: process.env.VUE_APP_APP_AUTHORITY,
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getRedirectUri(),
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false
  }
};

// Login request - minimal scopes
const loginRequest = {
  scopes: ["openid", "profile", "User.Read"]
};

// Token request for API calls
export const tokenRequest = {
  scopes: ["User.Read"]
};

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// State management
let isInitialized = false;
let isRedirecting = false;
let authCheckTimeout = null;

/**
 * Initialize MSAL instance
 * MUST be called before any other MSAL operation
 */
export async function initializeMsal() {
  if (isInitialized) {
    return;
  }

  try {
    // Initialize MSAL v3 - required before any other API call
    await msalInstance.initialize();

    // Handle redirect response
    const response = await msalInstance.handleRedirectPromise();

    if (response) {
      // Successfully authenticated via redirect
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
      isRedirecting = false;

      // Clean up URL by removing hash parameters
      if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    }

    isInitialized = true;
  } catch (error) {
    isInitialized = true; // Set to true to prevent infinite retries
    throw error;
  }
}

/**
 * Get the current active account
 */
export function getAccount() {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    // Set active account if not already set
    if (!msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]);
    }
    return accounts[0];
  }
  return null;
}

/**
 * Login with redirect
 */
export async function login() {
  if (!isInitialized) {
    return;
  }

  try {
    isRedirecting = true;
    await msalInstance.loginRedirect(loginRequest);
  } catch (error) {
    isRedirecting = false;
    throw error;
  }
}

/**
 * Logout
 */
export async function logout() {
  if (!isInitialized) {
    return;
  }

  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: getRedirectUri()
  });
}

/**
 * Acquire access token silently, with popup fallback
 */
export async function acquireAccessToken() {
  if (!isInitialized) {
    throw new Error("MSAL not initialized. Call initializeMsal() first.");
  }

  const account = getAccount();
  if (!account) {
    throw new Error("No account logged in");
  }

  try {
    // Try silent token acquisition first
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: account
    });
    return response.accessToken;
  } catch (error) {
    // Check if there's already an interaction in progress
    const inProgress = msalInstance.getActiveAccount();
    if (!inProgress) {
      return null;
    }

    // Fallback to popup for token acquisition
    const response = await msalInstance.acquireTokenPopup(tokenRequest);
    return response.accessToken;
  }
}

/**
 * Check access and handle authentication
 * Called by router guard
 */
export function checkAccess(next) {
  if (isRedirecting) {
    return;
  }

  // Check if we're in the middle of a redirect operation
  if (window.location.hash.includes("code=") || window.location.hash.includes("id_token")) {
    // Redirect is being handled, skip auth check
    return;
  }

  const loginDetails = getAccount();

  if (!loginDetails) {
    if (authCheckTimeout) {
      clearTimeout(authCheckTimeout);
    }

    // Double-check: sometimes account info takes a moment to be available after callback
    authCheckTimeout = setTimeout(() => {
      const account = getAccount();

      if (!account && !isRedirecting) {
        // No account, proceed with login redirect
        removeItem("loginDetails");
        setItem("[sln]authenticated", false);
        login();
      } else if (account) {
        // Account found, set authenticated state
        setItem("loginDetails", account);
        setItem("[sln]authenticated", true);
        store.dispatch("userAccount/hasAuthenticated", { loginDetails: account, isAuthenticated: true });
        store.dispatch("userAccount/getUserDetails");
        store.dispatch("userAccount/getGroupDetails");
        next();
      }

      authCheckTimeout = null;
    }, 100);
    return;
  } else {
    if (authCheckTimeout) {
      clearTimeout(authCheckTimeout);
      authCheckTimeout = null;
    }

    // User is authenticated
    setItem("loginDetails", loginDetails);
    setItem("[sln]authenticated", true);
    store.dispatch("userAccount/hasAuthenticated", { loginDetails: loginDetails, isAuthenticated: true });
    store.dispatch("userAccount/getUserDetails");
    store.dispatch("userAccount/getGroupDetails");
    return next();
  }
}
